/* URBRAND Collection Page — v5.9.83 */
(() => {
  'use strict';

  const instances = new WeakMap();
  const roots = new Set();
  const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  const debounce = (fn, delay = 280) => {
    let timer;
    const delayed = (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), delay);
    };
    delayed.cancel = () => window.clearTimeout(timer);
    return delayed;
  };

  const safeUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? url : null;
    } catch {
      return null;
    }
  };

  class CollectionPage {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.form = root.querySelector('[data-collection-filter-form]');
      this.grid = root.querySelector('[data-collection-product-grid]');
      this.results = root.querySelector('[data-collection-results]');
      this.abortController = new AbortController();
      this.requestController = null;
      this.infiniteObserver = null;
      this.lastTrigger = null;
      this.autoApply = root.dataset.autoApply === 'true';
      this.sizeFilterInStock = root.dataset.sizeFilterInStock === 'true';
      this.soldOutLast = root.dataset.soldOutLast === 'true';
      this.autoSubmit = debounce(() => this.applyForm(), 320);
      this.boundPopState = () => this.render(window.location.href, { historyMode: 'none', focusResults: false });
      this.bind();
      this.restoreView();
      this.sortSoldOutLast();
      this.setupInfiniteScroll();
      roots.add(root);

      const normalizedUrl = this.ensureSelectedSizeIsAvailable(new URL(window.location.href));
      if (normalizedUrl.href !== window.location.href) {
        this.render(normalizedUrl.href, { historyMode: 'replace', focusResults: false });
      }
    }

    bind() {
      const signal = this.abortController.signal;
      this.root.addEventListener('click', (event) => this.onClick(event), { signal });
      this.root.addEventListener('change', (event) => this.onChange(event), { signal });
      this.root.addEventListener('input', (event) => this.onInput(event), { signal });
      this.root.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      this.root.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      window.addEventListener('resize', debounce(() => this.onResize(), 140), { signal });
    }

    destroy() {
      this.autoSubmit.cancel();
      if (this.form?.classList.contains('is-open')) this.closeFilters(false);
      this.abortController.abort();
      this.requestController?.abort();
      this.infiniteObserver?.disconnect();
      roots.delete(this.root);
      instances.delete(this.root);
    }

    onClick(event) {
      const open = event.target.closest('[data-collection-filter-open]');
      if (open) {
        event.preventDefault();
        this.openFilters(open);
        return;
      }

      if (event.target.closest('[data-collection-filter-close]')) {
        event.preventDefault();
        this.closeFilters();
        return;
      }

      const viewButton = event.target.closest('[data-collection-view]');
      if (viewButton) {
        event.preventDefault();
        this.setView(viewButton.dataset.collectionView, true);
        return;
      }

      const facetLink = event.target.closest('[data-collection-facet-link]');
      if (facetLink) {
        const url = safeUrl(facetLink.href);
        if (!url) return;
        event.preventDefault();
        this.closeFilters(false);
        this.render(url.href, { historyMode: 'push', focusResults: true });
        return;
      }

      const loadMore = event.target.closest('[data-collection-load-more]');
      if (loadMore) {
        const url = safeUrl(loadMore.href);
        if (!url) return;
        event.preventDefault();
        this.loadMore(url.href, loadMore);
        return;
      }

      const descriptionToggle = event.target.closest('[data-description-toggle]');
      if (descriptionToggle) {
        const description = descriptionToggle.closest('[data-collection-description]');
        if (!description) return;
        const expanded = description.classList.toggle('is-expanded');
        descriptionToggle.setAttribute('aria-expanded', String(expanded));
        const more = descriptionToggle.querySelector('[data-description-more]');
        const less = descriptionToggle.querySelector('[data-description-less]');
        if (more) more.hidden = expanded;
        if (less) less.hidden = !expanded;
      }
    }

    onChange(event) {
      const sort = event.target.closest('[data-collection-sort]');
      if (sort) {
        this.applyForm({ focusResults: true });
        return;
      }

      if (event.target.matches('[data-collection-filter-input]') && this.autoApply) {
        this.autoSubmit();
      }
    }

    onInput(event) {
      if (!event.target.matches('[data-collection-filter-input]')) return;
      if (event.target.type === 'number' && this.autoApply) this.autoSubmit();
    }

    onSubmit(event) {
      if (!event.target.matches('[data-collection-filter-form]')) return;
      event.preventDefault();
      this.closeFilters(false);
      this.applyForm({ focusResults: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.form?.classList.contains('is-open')) {
        event.preventDefault();
        this.closeFilters();
        return;
      }
      if (event.key !== 'Tab' || !this.form?.classList.contains('is-open')) return;
      const panel = this.form.querySelector('.urb-collection__facets-panel');
      const focusables = panel ? [...panel.querySelectorAll(FOCUSABLE)].filter((node) => !node.hidden && node.offsetParent !== null) : [];
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openFilters(trigger) {
      if (!this.form) return;
      this.lastTrigger = trigger;
      this.form.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('locked');
      const panel = this.form.querySelector('.urb-collection__facets-panel');
      panel?.setAttribute('role', 'dialog');
      panel?.setAttribute('aria-modal', 'true');
      window.setTimeout(() => {
        (panel?.querySelector(FOCUSABLE) || panel)?.focus({ preventScroll: true });
      }, 60);
    }

    closeFilters(restoreFocus = true) {
      if (!this.form) return;
      this.form.classList.remove('is-open');
      const panel = this.form.querySelector('.urb-collection__facets-panel');
      panel?.removeAttribute('role');
      panel?.removeAttribute('aria-modal');
      this.root.querySelector('[data-collection-filter-open]')?.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('dialog[open],.mobile-menu.open,.urb-collection__facets.is-open')) {
        document.body.classList.remove('locked');
      }
      if (restoreFocus) this.lastTrigger?.focus({ preventScroll: true });
    }

    onResize() {
      if (window.innerWidth >= 750 && !this.root.classList.contains('urb-collection--filters-drawer') && this.form?.classList.contains('is-open')) {
        this.closeFilters(false);
      }
    }

    buildUrl() {
      if (!this.form) return new URL(window.location.href);
      const base = safeUrl(this.form.dataset.baseUrl || window.location.pathname) || new URL(window.location.href);
      const params = new URLSearchParams();
      const data = new FormData(this.form);
      for (const [key, rawValue] of data.entries()) {
        const value = String(rawValue).trim();
        if (value !== '') params.append(key, value);
      }
      params.delete('page');
      const url = new URL(base.pathname, window.location.origin);
      url.search = params.toString();
      return this.ensureSelectedSizeIsAvailable(url);
    }

    ensureSelectedSizeIsAvailable(url) {
      if (!this.sizeFilterInStock) return url;
      const hasSelectedSize = [...url.searchParams.keys()].some((key) => {
        if (!key.startsWith('filter.v.option.')) return false;
        const optionName = key
          .slice('filter.v.option.'.length)
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();
        return ['tamanho', 'size', 'talla', 'taille'].some((label) => optionName.includes(label));
      });
      if (hasSelectedSize && !url.searchParams.has('filter.v.availability')) {
        url.searchParams.set('filter.v.availability', '1');
      }
      return url;
    }

    applyForm({ focusResults = true } = {}) {
      this.render(this.buildUrl().href, { historyMode: 'push', focusResults });
    }

    setLoading(loading) {
      this.root.classList.toggle('is-loading', loading);
      this.results?.setAttribute('aria-busy', String(loading));
    }

    async fetchSection(url) {
      if (!this.sectionId) throw new Error('Missing section id');
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      const target = safeUrl(url);
      if (!target) throw new Error('Invalid URL');
      target.searchParams.set('section_id', this.sectionId);
      const response = await fetch(target.href, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const documentPage = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (controller.signal.aborted || this.requestController !== controller) throw new DOMException('Superseded request', 'AbortError');
      const freshRoot = documentPage.querySelector('[data-urb-collection]');
      if (!freshRoot) throw new Error('Collection section missing');
      return freshRoot;
    }

    async render(url, { historyMode = 'push', focusResults = true } = {}) {
      const revision = this.renderRevision = (this.renderRevision || 0) + 1;
      this.setLoading(true);
      try {
        const freshRoot = await this.fetchSection(url);
        const cleanUrl = safeUrl(url);
        if (cleanUrl) cleanUrl.searchParams.delete('section_id');
        if (cleanUrl && historyMode === 'push') window.history.pushState({}, '', cleanUrl.href);
        if (cleanUrl && historyMode === 'replace') window.history.replaceState({}, '', cleanUrl.href);

        this.destroy();
        this.root.replaceWith(freshRoot);
        window.URB?.refresh?.(freshRoot);
        const freshInstance = initRoot(freshRoot);
        if (focusResults) {
          const target = freshRoot.querySelector('[data-collection-results]');
          target?.setAttribute('tabindex', '-1');
          target?.focus({ preventScroll: true });
          target?.scrollIntoView({ behavior: REDUCE_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
        }
        return freshInstance;
      } catch (error) {
        if (error.name === 'AbortError') return null;
        const fallback = safeUrl(url);
        if (fallback) window.location.assign(fallback.href);
        return null;
      } finally {
        if (this.renderRevision === revision) this.setLoading(false);
      }
    }

    async loadMore(url, button) {
      if (button.classList.contains('is-loading')) return;
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      this.infiniteObserver?.disconnect();
      try {
        const freshRoot = await this.fetchSection(url);
        const freshGrid = freshRoot.querySelector('[data-collection-product-grid]');
        const freshPagination = freshRoot.querySelector('[data-collection-pagination]');
        if (!freshGrid || !this.grid) throw new Error('Product grid missing');

        const known = new Set([...this.grid.querySelectorAll('[data-collection-product]')].map((node) => node.dataset.productId));
        const incoming = [...freshGrid.children].filter((node) => {
          if (!node.matches('[data-collection-product]')) return true;
          const id = node.dataset.productId;
          if (!id || known.has(id)) return false;
          known.add(id);
          return true;
        });
        const fragment = document.createDocumentFragment();
        incoming.forEach((node) => fragment.appendChild(node));
        this.grid.appendChild(fragment);

        const currentPagination = this.root.querySelector('[data-collection-pagination]');
        if (currentPagination && freshPagination) currentPagination.replaceWith(freshPagination);
        else currentPagination?.remove();

        this.sortSoldOutLast();

        window.URB?.refresh?.(this.grid);
        this.announce(incoming.filter((node) => node.matches('[data-collection-product]')).length);
        this.setupInfiniteScroll();
      } catch (error) {
        if (error.name !== 'AbortError') window.location.assign(url);
      } finally {
        if (document.contains(button)) {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
        }
      }
    }

    announce(count) {
      if (!count) return;
      let status = this.root.querySelector('[data-collection-live-status]');
      if (!status) {
        status = document.createElement('p');
        status.className = 'urb-collection__sr-status';
        status.dataset.collectionLiveStatus = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        this.root.appendChild(status);
      }
      status.textContent = (this.root.dataset.loadedText || '__COUNT__ products loaded.').replace('__COUNT__', String(count));
    }

    sortSoldOutLast() {
      if (!this.soldOutLast || !this.grid) return;
      const soldOutProducts = this.grid.querySelectorAll('[data-collection-product][data-product-available="false"]');
      soldOutProducts.forEach((product) => this.grid.appendChild(product));
      if (!this.root.querySelector('[data-collection-load-more]')) {
        soldOutProducts.forEach((product) => {
          product.hidden = false;
          product.removeAttribute('data-product-sold-out-deferred');
        });
      }
    }

    setupInfiniteScroll() {
      this.infiniteObserver?.disconnect();
      const button = this.root.querySelector('[data-collection-load-more][data-mode="infinite"]');
      if (!button || !('IntersectionObserver' in window)) return;
      this.infiniteObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore(button.href, button);
      }, { rootMargin: '600px 0px' });
      this.infiniteObserver.observe(button);
    }

    restoreView() {
      const defaultView = this.root.dataset.defaultView || 'grid';
      let stored = null;
      try { stored = sessionStorage.getItem('urbrand:collection-view'); } catch { /* blocked */ }
      this.setView(stored || defaultView, false);
    }

    setView(view, persist) {
      if (!['grid', 'list'].includes(view) || !this.grid) return;
      this.grid.classList.toggle('is-grid', view === 'grid');
      this.grid.classList.toggle('is-list', view === 'list');
      this.grid.dataset.view = view;
      this.root.querySelectorAll('[data-collection-view]').forEach((button) => {
        const active = button.dataset.collectionView === view;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      if (persist) {
        try { sessionStorage.setItem('urbrand:collection-view', view); } catch { /* blocked */ }
      }
    }
  }

  function initRoot(root) {
    if (!(root instanceof Element) || instances.has(root)) return instances.get(root);
    const instance = new CollectionPage(root);
    instances.set(root, instance);
    return instance;
  }

  function init(scope = document) {
    if (scope instanceof Element && scope.matches('[data-urb-collection]')) initRoot(scope);
    scope.querySelectorAll?.('[data-urb-collection]').forEach(initRoot);
  }

  init();

  window.addEventListener('popstate', () => {
    [...roots].forEach((root) => instances.get(root)?.render(window.location.href, { historyMode: 'none', focusResults: false }));
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-urb-collection]') ? event.target : event.target.querySelector?.('[data-urb-collection]');
    if (root) instances.get(root)?.destroy();
  });
})();
